import { Component, OnDestroy, OnInit, computed, signal, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { appRoutePaths } from '../../../../app.paths';

import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { Subject, Subscription, debounceTime, finalize } from 'rxjs';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiTextfield } from 'omnium-ui/form-field';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { OuiTableModule } from 'omnium-ui/table';
import { ConsultantCardComponent } from '../../../../core/components/consultant-card/consultant-card.component';
import { InvestorCardComponent } from '../../../../core/components/investor-card/investor-card.component';
import { InvestorCrossConsultantRelationsPage, InvestorCrossConsultantRelationsRow } from '../../../../core/models/api.models';
import { DoublonsBusterApiService } from '../../../../core/services/doublons-buster-api.service';
import { InvestorStatusTagComponent } from '../../shared/investor-status-tag/investor-status-tag.component';
import { StateCardComponent } from '../../shared/state-card/state-card.component';
import { createFrenchPaginatorIntl } from '../shared/atlas-list-page.paginator';

@Component({
  selector: 'dbw-cross-consultant-relations-page',
  imports: [
    MatPaginatorModule,
    MatTableModule,
    RouterLink,
    OuiButtonModule,
    OuiTextfield,
    OuiTableModule,
    ConsultantCardComponent,
    InvestorCardComponent,
    InvestorStatusTagComponent,
    StateCardComponent,
  ],
  providers: [
    {
      provide: MatPaginatorIntl,
      useFactory: createFrenchPaginatorIntl,
    },
  ],
  templateUrl: './cross-consultant-relations.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['../shared/atlas-duplicate-list-page.shared.scss'],
})
export class CrossConsultantRelationsPageComponent implements OnInit, OnDestroy {
  readonly appRoutePaths = appRoutePaths;
  readonly displayedColumns = ['investor', 'status', 'consultant', 'relationKinds', 'relatedInvestors'];
  readonly pageSizeOptions = [10, 25, 50, 100];
  readonly consultantIdFilter = signal('');
  readonly consultantCodeFilter = signal('');
  readonly consultantNameFilter = signal('');
  readonly loading = signal(true);
  readonly exporting = signal(false);
  readonly error = signal<string | undefined>(undefined);
  readonly page = signal<InvestorCrossConsultantRelationsPage | undefined>(undefined);
  readonly pageIndex = signal(0);
  readonly pageSize = signal(25);

  private readonly filterChanges = new Subject<void>();
  private loadSubscription?: Subscription;
  private exportSubscription?: Subscription;
  private filterSubscription?: Subscription;

  constructor(
    private readonly apiService: DoublonsBusterApiService,
    private readonly snackbarService: OuiSnackbarService
  ) {}

  ngOnInit(): void {
    this.filterSubscription = this.filterChanges.pipe(debounceTime(300)).subscribe(() => {
      this.pageIndex.set(0);
      this.loadPage();
    });

    this.loadPage();
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.exportSubscription?.unsubscribe();
    this.filterSubscription?.unsubscribe();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
    this.loadPage();
  }

  reload(): void {
    this.loadPage();
  }

  exportCsv(): void {
    if (this.exporting()) {
      return;
    }

    this.exporting.set(true);
    this.exportSubscription?.unsubscribe();
    this.exportSubscription = this.apiService
      .exportInvestorsWithCrossConsultantRelations({
        consultantId: this.parseOptionalNumber(this.consultantIdFilter()),
        consultantCode: this.consultantCodeFilter(),
        consultantName: this.consultantNameFilter(),
      })
      .pipe(finalize(() => this.exporting.set(false)))
      .subscribe({
        next: blob => {
          const downloadUrl = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = downloadUrl;
          anchor.download = `relations-inter-consultants-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
          anchor.click();
          URL.revokeObjectURL(downloadUrl);
          this.snackbarService.open('Export CSV terminé.', 'success');
        },
        error: () => {
          this.snackbarService.open("L'export CSV a échoué.", 'error');
        },
      });
  }

  onConsultantIdFilterChange(value: string): void {
    this.consultantIdFilter.set(value);
    this.queueFilterReload();
  }

  onConsultantCodeFilterChange(value: string): void {
    this.consultantCodeFilter.set(value);
    this.queueFilterReload();
  }

  onConsultantNameFilterChange(value: string): void {
    this.consultantNameFilter.set(value);
    this.queueFilterReload();
  }

  formatRelationKinds(values: string[]): string {
    return values.join(' • ');
  }

  formatConsultantLabel(code?: string | null, name?: string | null): string {
    const label = [code?.trim(), name?.trim()].filter((value): value is string => !!value).join(' ');

    return label || '-';
  }

  private getCurrentPageRelatedInvestorCount(): number {
    return new Set((this.page()?.items ?? []).flatMap(row => row.relatedInvestors.map(relatedInvestor => relatedInvestor.investorId))).size;
  }

  private loadPage(): void {
    this.loading.set(true);
    this.error.set(undefined);
    this.loadSubscription?.unsubscribe();

    this.loadSubscription = this.apiService
      .getInvestorsWithCrossConsultantRelationsPage(this.pageIndex(), this.pageSize(), {
        consultantId: this.parseOptionalNumber(this.consultantIdFilter()),
        consultantCode: this.consultantCodeFilter(),
        consultantName: this.consultantNameFilter(),
      })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: page => {
          this.page.set(page);
          this.pageIndex.set(page.pageIndex);
          this.pageSize.set(page.pageSize);
        },
        error: error => {
          this.page.set(undefined);
          this.error.set(error?.error?.detail ?? 'Impossible de charger les relations inter-consultants.');
        },
      });
  }

  private queueFilterReload(): void {
    this.filterChanges.next();
  }

  private parseOptionalNumber(value: string): number | null {
    const trimmed = value.trim();

    if (!trimmed) {
      return null;
    }

    const parsedValue = Number(trimmed);

    return Number.isInteger(parsedValue) && parsedValue >= 0 ? parsedValue : null;
  }

  private getActiveFiltersSummary(): string {
    const parts: string[] = [];

    if (this.consultantIdFilter().trim()) {
      parts.push(`ConsultantId=${this.consultantIdFilter().trim()}`);
    }

    if (this.consultantCodeFilter().trim()) {
      parts.push(`Code consultant=${this.consultantCodeFilter().trim()}`);
    }

    if (this.consultantNameFilter().trim()) {
      parts.push(`Nom consultant~${this.consultantNameFilter().trim()}`);
    }

    return parts.join(' • ');
  }

  protected trackRelatedInvestor(_index: number, row: InvestorCrossConsultantRelationsRow): string {
    return `${row.investorId}-${row.relatedInvestorCount}-${row.relationCount}`;
  }
}
